import {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { toast } from 'sonner';
import { StatusBadge } from './StatusBadge';
import { ChannelBadge } from './ChannelBadge';
import { DraftingEmpty } from './DraftingEmpty';
import { PageTabs } from './PageTabs';
import { PreviewFrame } from './PreviewFrame';
import { FileEditor } from './FileEditor';
import { CommentComposer, renderReviewBody } from './CommentComposer';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import type { EditCommentPayload } from '@/lib/iframeMessages';
import { ActionButtons } from './ActionButtons';
import {
  editorViewReducer, INITIAL_EDITOR_VIEW,
} from '@/lib/editorViewState';
import { api } from '@/lib/api';
import { useIframeComments } from '@/lib/useIframeComments';
import type { OutboundMessage } from '@/lib/iframeMessages';
import type { DashboardItem, ItemStatus } from '@shared/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  activePageForPrSelector,
  approvedPagesForPrSelector,
  commentsForPrSelector,
} from '@/store/selectors';
import {
  addPrComment,
  removePrComment,
  updatePrComment,
  clearPrComments,
} from '@/store/slices/commentsSlice';
import { setActivePage, toggleApprovedPage } from '@/store/slices/reviewSlice';

function formatCommentBody(
  kind: 'comment' | 'suggest',
  selection: string,
  body: string,
  nearestHeading?: string,
  contextSnippet?: string,
): string {
  const sectionPrefix = nearestHeading ? ` in section "${nearestHeading}"` : '';
  const contextSuffix = contextSnippet ? `\n\n  > _Context: ${contextSnippet}_` : '';
  return kind === 'suggest'
    ? `**Suggested edit**${sectionPrefix} — replace "${selection}" with: ${body}${contextSuffix}`
    : `**Comment**${sectionPrefix} on "${selection}" — ${body}${contextSuffix}`;
}

function mdxPathForPage(url: string): string | null {
  try {
    const u = new URL(url);
    const parts = u.pathname.replace(/^\/+|\/+$/g, '').split('/');
    if (parts.length !== 3) return null;
    const [locale, cat, slug] = parts;
    if (!/^[a-z]{2}$/.test(locale)) return null;
    return `content/${locale}/${cat}/${slug}.mdx`;
  } catch {
    return null;
  }
}

interface Props {
  item: DashboardItem;
  token: string;
  onAfterAction: () => void;
  onOptimisticStatus: (status: ItemStatus) => void;
  // App registers this to intercept its own navigation (e.g. sidebar PR selection)
  // so we can show the unsaved-changes guard when the editor is dirty.
  registerNavigationGuard?: (guard: (action: () => void) => void) => void;
}

export function DetailPane({
  item, token, onAfterAction, onOptimisticStatus, registerNavigationGuard,
}: Props) {
  const dispatch = useAppDispatch();
  const pages = item.pr?.pages ?? [];

  const approvedArray = useAppSelector(approvedPagesForPrSelector(item.number));
  const approvedPages = useMemo(() => new Set(approvedArray), [approvedArray]);
  const active = useAppSelector(activePageForPrSelector(item.number));
  const comments = useAppSelector(commentsForPrSelector(item.number));

  const [editorDirty, setEditorDirty] = useState(false);
  const [pendingNav, setPendingNav] = useState<{ action: () => void } | null>(null);

  const guardNavigation = useCallback((action: () => void) => {
    if (editorDirty) {
      setPendingNav({ action });
    } else {
      action();
    }
  }, [editorDirty]);

  useEffect(() => {
    registerNavigationGuard?.(guardNavigation);
    return () => registerNavigationGuard?.((a) => a());
  }, [guardNavigation, registerNavigationGuard]);

  const changeActive = (i: number) => {
    guardNavigation(() => {
      dispatch(setActivePage({ prNumber: item.number, index: i }));
    });
  };

  const toggleApproved = (i: number) => {
    dispatch(toggleApprovedPage({ prNumber: item.number, pageIndex: i }));
  };

  // editingPath/inEditView live in a reducer so the Preview/Edit transitions
  // stay correct when the active article changes — see editorViewState.ts.
  const [{ editingPath, inEditView }, dispatchEditor] = useReducer(
    editorViewReducer,
    INITIAL_EDITOR_VIEW,
  );

  const iframeWindowRef = useRef<Window | null>(null);
  const pendingEditRef = useRef<EditCommentPayload | null>(null);
  const iframeReadyRef = useRef(false);

  const sendToIframe = (msg: OutboundMessage) => {
    iframeWindowRef.current?.postMessage(msg, '*');
  };

  useIframeComments({
    onComment: ({
      kind, selection, body, pagePath, anchorId, nearestHeading, contextSnippet,
    }) => {
      const formatted = formatCommentBody(kind, selection, body, nearestHeading, contextSnippet);
      const matchingPage = pages.find((p) => {
        try {
          return new URL(p.url).pathname === pagePath;
        } catch {
          return false;
        }
      });
      dispatch(addPrComment({
        prNumber: item.number,
        comment: {
          body: formatted,
          pageTitle: matchingPage?.title,
          pagePath,
          anchorId,
          anchorData: { kind, selection, rawBody: body, nearestHeading, contextSnippet },
        },
      }));
    },
    onReady: (readyPath, source) => {
      // Ignore stale ready signals from pages we've already navigated away from.
      try {
        if (new URL(pageUrl).pathname !== readyPath) return;
      } catch { return; }
      iframeWindowRef.current = source;
      iframeReadyRef.current = true;
      if (pendingEditRef.current) {
        sendToIframe({ type: 'kb-dashboard:edit-comment', payload: pendingEditRef.current });
        pendingEditRef.current = null;
      }
    },
    onRequestEdit: (anchorId) => {
      const comment = comments.find((c) => c.anchorId === anchorId);
      if (!comment?.anchorData) return;
      const payload: EditCommentPayload = {
        anchorId,
        rawBody: comment.anchorData.rawBody,
        kind: comment.anchorData.kind,
        selection: comment.anchorData.selection,
      };
      sendToIframe({ type: 'kb-dashboard:edit-comment', payload });
    },
    onUpdateComment: (anchorId, rawBody) => {
      const comment = comments.find((c) => c.anchorId === anchorId);
      if (!comment?.anchorData) return;
      const { kind, selection, nearestHeading, contextSnippet } = comment.anchorData;
      dispatch(updatePrComment({
        prNumber: item.number,
        anchorId,
        rawBody,
        formattedBody: formatCommentBody(kind, selection, rawBody, nearestHeading, contextSnippet),
      }));
    },
  });

  const submitReview = async (event: 'REQUEST_CHANGES' | 'APPROVE') => {
    if (item.kind !== 'pr') return;
    const body =
      event === 'APPROVE' ? 'Publish' : renderReviewBody(comments);
    try {
      await api.submitReview(token, item.number, { event, body });
      if (event === 'REQUEST_CHANGES') {
        dispatch(clearPrComments({ prNumber: item.number }));
        sendToIframe({
          type: 'kb-dashboard:clear-highlights',
          payload: {},
        });
      }
      onOptimisticStatus(event === 'APPROVE' ? 'publishing' : 'building');
      onAfterAction();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Submit failed';
      toast.error(
        event === 'APPROVE' ? 'Publish failed' : 'Request changes failed',
        { description: msg },
      );
    }
  };

  const isPrWithPreview =
    item.kind === 'pr' && (item.pr?.previewReady ?? false) && pages.length > 0;
  const safeActive = Math.min(active, Math.max(0, pages.length - 1));

  // Compute preview origin once for both FileEditor and MdxPreview
  const pageUrl = pages[safeActive]?.url ?? '';
  let previewOrigin = '';
  try { previewOrigin = new URL(pageUrl).origin; } catch {}

  // Leave the editor whenever the active page changes — a different article
  // tab, or a different PR. Without this, "Edit MDX" on the new page kept
  // showing the previously-opened article's MDX.
  // Also reset iframe readiness so edit-comment is queued until the overlay
  // re-sends ready on the newly loaded page.
  useEffect(() => {
    dispatchEditor({ type: 'page-changed' });
    iframeReadyRef.current = false;
    setEditorDirty(false);
  }, [item.number, pageUrl]);

  return (
    <main className="flex-1 flex flex-col h-screen min-w-0">
      <header className="px-4 py-3 border-b flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={item.status} />
            <ChannelBadge baseBranch={item.baseBranch} />
            <a
              href={item.htmlUrl} target="_blank" rel="noreferrer"
              className="text-xs text-muted-foreground hover:underline"
            >
              #{item.number} on GitHub
            </a>
          </div>
          <h2 className="font-semibold truncate">{item.title}</h2>
        </div>
        <ActionButtons
          item={item}
          hasPendingComments={comments.length > 0}
          onRequestChanges={() => submitReview('REQUEST_CHANGES')}
          onPublish={() => submitReview('APPROVE')}
          readyCount={approvedPages.size}
          totalPages={pages.length}
        />
      </header>

      {!isPrWithPreview ? (
        <DraftingEmpty item={item} />
      ) : (
        <>
          <PageTabs
            pages={pages}
            active={safeActive}
            onChange={changeActive}
            approvedPages={approvedPages}
            onToggleApproved={toggleApproved}
          />
          <div className="flex items-center gap-2 px-3 py-1 border-b text-xs">
            <button
              type="button"
              className={!editingPath || !inEditView ? 'font-semibold' : 'text-muted-foreground hover:underline'}
              onClick={() => dispatchEditor({ type: 'show-preview' })}
            >
              Preview
            </button>
            <span className="text-muted-foreground">·</span>
            <button
              type="button"
              className={editingPath && inEditView ? 'font-semibold' : 'text-muted-foreground hover:underline'}
              onClick={() => {
                const url = pages[safeActive]?.url ?? '';
                const path = mdxPathForPage(url);
                if (!path) {
                  toast.error('Cannot derive file path for this page.');
                  return;
                }
                // Always target the page you're looking at. Re-opening the same
                // file (after toggling to Preview) keeps editingPath unchanged,
                // so the editor stays mounted and pasted-image blobs survive.
                dispatchEditor({ type: 'open-edit', pagePath: path });
              }}
            >
              Edit MDX
            </button>
          </div>
          {editingPath ? (
            <>
              {/* FileEditor stays mounted whenever editingPath is set so the
                  in-memory pasted-image blob URLs survive view toggles.
                  Hidden via display:none when showing the Preview iframe. */}
              <div className={inEditView ? 'flex-1 flex flex-col min-h-0' : 'hidden'}>
                <FileEditor
                  mode={{ kind: 'existing', prNumber: item.number }}
                  token={token}
                  filePath={editingPath}
                  previewOrigin={previewOrigin}
                  onDirtyChange={setEditorDirty}
                  onSaveCommit={(entry) => {
                    dispatch(addPrComment({
                      prNumber: item.number,
                      comment: { body: '[editor save]', editorSave: entry },
                    }));
                  }}
                  onAuthError={() => onAfterAction()}
                />
              </div>
              {!inEditView && (
                <PreviewFrame
                  page={pages[safeActive]}
                  cacheBust={item.pr?.headSha ?? ''}
                  onIframeReady={(win) => { iframeWindowRef.current = win; }}
                />
              )}
            </>
          ) : (
            <PreviewFrame
              page={pages[safeActive]}
              cacheBust={item.pr?.headSha ?? ''}
              onIframeReady={(win) => { iframeWindowRef.current = win; }}
            />
          )}
          <CommentComposer
            pages={pages}
            activePageIndex={safeActive}
            comments={comments}
            onAdd={(comment) => dispatch(addPrComment({ prNumber: item.number, comment }))}
            onRemove={(index) => {
              const removed = comments[index];
              dispatch(removePrComment({ prNumber: item.number, index }));
              if (removed?.anchorId) {
                sendToIframe({
                  type: 'kb-dashboard:remove-highlight',
                  payload: { anchorId: removed.anchorId, pagePath: removed.pagePath },
                });
              }
            }}
            onClickComment={(comment) => {
              if (!comment.anchorId || !comment.anchorData) return;
              const payload: EditCommentPayload = {
                anchorId: comment.anchorId,
                rawBody: comment.anchorData.rawBody,
                kind: comment.anchorData.kind,
                selection: comment.anchorData.selection,
              };
              if (comment.pagePath) {
                const pageIndex = pages.findIndex((p) => {
                  try { return new URL(p.url).pathname === comment.pagePath; } catch { return false; }
                });
                if (pageIndex !== -1 && pageIndex !== safeActive) {
                  pendingEditRef.current = payload;
                  changeActive(pageIndex);
                  return;
                }
              }
              if (!iframeReadyRef.current) {
                pendingEditRef.current = payload;
                return;
              }
              sendToIframe({ type: 'kb-dashboard:edit-comment', payload });
            }}
          />
        </>
      )}

      <Dialog open={!!pendingNav} onOpenChange={(o) => !o && setPendingNav(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              Your edits haven't been saved and will be lost if you continue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingNav(null)}>
              Stay here
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const action = pendingNav!.action;
                setPendingNav(null);
                action();
              }}
            >
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
